// src/belediye/components/steps/ReviewOrForwardStep.js
import React, { useState, useEffect } from "react";
// FAZ-13: izin cutover'i
import usePermissions from "../../hooks/usePermissions";
import { useDispatch } from "react-redux";

import {
  reviewApplication,
  forwardApplication,
  createForwardLog,
} from "../../redux/slices/belediyeApplicationDetailsSlice";

import api from "../../services/belediyeApi";

// Heroicons
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathRoundedSquareIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";

const ReviewOrForwardStep = ({
  applicationId,
  applicationData,
  panelData,
  updatePanelData,
  onNext,
  isFirstStep,
  isLastStep,
  unitSlug,
  goToStep,
}) => {
  const dispatch = useDispatch();
  // FAZ-13: islem turu kartlari izne bagli (uiOn=false → ikisi de gorunur, eski davranis)
  const { uiOn, can } = usePermissions();
  const canReview = uiOn ? can("applications.status.change") : true;
  const canForward = uiOn ? can("applications.forward") : true;

  const user = JSON.parse(localStorage.getItem("user"));

  /* ---------------------------------------------
     LOCAL STATE
  --------------------------------------------- */
  const [actionType, setActionType] = useState(panelData.reviewType || "review");
  const [forwardUnitId, setForwardUnitId] = useState(
    panelData.forwardUnitId || ""
  );
  const [forwardNote, setForwardNote] = useState(panelData.forwardNote || "");
  const [units, setUnits] = useState([]);

  const [loadingUnits, setLoadingUnits] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [isStepLocked, setIsStepLocked] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  /* ---------------------------------------------
     CONFIRM MODAL STATE
  --------------------------------------------- */
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // "review" | "forward"

  /* ---------------------------------------------
     STEP LOCK (statüye göre)
  --------------------------------------------- */
  useEffect(() => {
    if (
      applicationData?.status === "işleme_alındı" ||
      applicationData?.status === "çözüldü" ||
      applicationData?.status === "çözülemedi" ||
      applicationData?.was_forwarded
    ) {
      setIsStepLocked(true);

      // Uyarı mesajı + işlemi yapan kişi
      if (applicationData.was_forwarded) {
        setSuccessMsg("Başvuru başarıyla yönlendirildi.");
      } else {
        setSuccessMsg(
          `Başvuru incelemeye alındı. İşlemi yapan: ${
            user?.name + " " + user?.surname
          }`
        );
      }
    }
  }, [applicationData]);

  /* ---------------------------------------------
     BİRİMLERİ ÇEK (yalnızca forward seçilince)
  --------------------------------------------- */
  useEffect(() => {
    if (actionType !== "forward") return;

    setLoadingUnits(true);
    api
      .get("/common/units/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      })
      .then((res) => {
        setUnits(res.data);
      })
      .catch(() => setError("Birimler yüklenemedi."))
      .finally(() => setLoadingUnits(false));
  }, [actionType]);

  /* ---------------------------------------------
     PANEL DATA'YI PARENTE GÖNDER
  --------------------------------------------- */
  useEffect(() => {
    updatePanelData({
      reviewType: actionType,
      forwardUnitId: actionType === "forward" ? forwardUnitId : null,
      forwardNote: actionType === "forward" ? forwardNote : "",
    });
    // eslint-disable-next-line
  }, [actionType, forwardUnitId, forwardNote]);

  /* ---------------------------------------------
     HELPERS
  --------------------------------------------- */
  const isForwardValid = () =>
    actionType !== "forward" || (forwardUnitId && forwardNote.trim());

  /* ---------------------------------------------
     İNCELEME İŞLEMİ
  --------------------------------------------- */
  const handleReview = async () => {
    setProcessing(true);
    setError("");

    try {
      await dispatch(
        reviewApplication({
          id: applicationId,
          data: { status: "işleme_alındı" },
          token: localStorage.getItem("accessToken"),
        })
      ).unwrap();

      setSuccessMsg(
        `Başvuru incelemeye alındı. İşlemi yapan: ${
          user?.name + " " + user?.surname
        }`
      );
      setIsStepLocked(true);

      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      setError("Başvuru incelemeye alınamadı!");
    }
    setProcessing(false);
  };

  /* ---------------------------------------------
     YÖNLENDİRME İŞLEMİ
  --------------------------------------------- */
  const handleForward = async () => {
    setProcessing(true);
    setError("");

    try {
      await dispatch(
        forwardApplication({
          id: applicationId,
          data: {
            old_unit: applicationData.unit,
            new_unit: forwardUnitId,
            note: forwardNote,
          },
          token: localStorage.getItem("accessToken"),
        })
      ).unwrap();

      await dispatch(
        createForwardLog({
          application: applicationId,
          old_unit: applicationData.unit,
          new_unit: forwardUnitId,
          note: forwardNote,
          token: localStorage.getItem("accessToken"),
        })
      ).unwrap();

      setSuccessMsg("Başvuru başarıyla yönlendirildi.");
      setIsStepLocked(true);

      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      setError("Başvuru yönlendirme başarısız oldu.");
    }

    setProcessing(false);
  };

  /* ---------------------------------------------
     MODAL ONAY (İŞLEMİ BAŞLATAN NOKTA)
  --------------------------------------------- */
  const openConfirm = (type) => {
    setConfirmType(type);
    setShowConfirm(true);
  };

  const confirmAction = async () => {
    setShowConfirm(false);

    if (confirmType === "review") {
      await handleReview();
    } else if (confirmType === "forward") {
      await handleForward();
    }
  };

  /* ---------------------------------------------
     RENDER
  --------------------------------------------- */
  return (
    <div className="aap-step-review">
      {/* -------- Başlık -------- */}
      <div className="aap-step-header">
        <h3 className="aap-step-title">İşlem Türü Seçimi</h3>

        <div className="aap-step-info">
          <InformationCircleIcon className="aap-step-info-icon" />
          <span>
            Başvuruyu incelemeye alabilir veya başka bir birime yönlendirebilirsiniz.
            İşlemler geri alınamaz; lütfen dikkatli seçim yapın.
          </span>
        </div>
      </div>

      {/* -------- Success / Error Messages -------- */}
      {successMsg && (
        <div className="aap-alert aap-alert-success">{successMsg}</div>
      )}

      {error && <div className="aap-alert aap-alert-error">{error}</div>}

      {/* -------- Seçim Kartları -------- */}
      <div className="aap-choice-container">
        {/* İncelemeye Al — FAZ-13: uiOn'da applications.status.change izni */}
        {canReview && (
        <div
          className={`aap-choice-box ${
            actionType === "review" ? "active" : ""
          } ${isStepLocked ? "disabled" : ""}`}
          onClick={() => !isStepLocked && setActionType("review")}
        >
          <h4>
            <ClipboardDocumentCheckIcon
              style={{
                width: 20,
                height: 20,
                marginRight: 6,
                verticalAlign: "middle",
              }}
            />
            İncelemeye Al
          </h4>
          <p>
            Başvuruyu resmi olarak işleme alır ve süreci başlatır. Geri dönüş
            yapılamaz.
          </p>
        </div>
        )}

        {/* Yönlendirme — FAZ-13: uiOn'da applications.forward izni */}
        {canForward && (
        <div
          className={`aap-choice-box ${
            actionType === "forward" ? "active forward" : ""
          } ${isStepLocked ? "disabled" : ""}`}
          onClick={() => !isStepLocked && setActionType("forward")}
        >
          <h4>
            <ArrowPathRoundedSquareIcon
              style={{
                width: 20,
                height: 20,
                marginRight: 6,
                verticalAlign: "middle",
              }}
            />
            Başka Birime Yönlendir
          </h4>
          <p>Başvuruyu farklı bir birime aktarır. Bu işlem geri alınamaz.</p>
        </div>
        )}
      </div>

      {/* -------- Yönlendirme Formu -------- */}
      {actionType === "forward" && (
        <div className="aap-forward-card">
          <div className="aap-field">
            <label className="aap-field-label">
              Hedef Birim <span className="aap-required">*</span>
            </label>

            <select
              className="aap-field-control"
              disabled={loadingUnits || isStepLocked}
              value={forwardUnitId}
              onChange={(e) => setForwardUnitId(e.target.value)}
            >
              <option value="">Seçiniz</option>
              {units
                .filter((u) => u.id !== applicationData.unit)
                .map((u) => (
                  <option value={u.id} key={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="aap-field">
            <label className="aap-field-label">
              Yönlendirme Notu <span className="aap-required">*</span>
            </label>
            <textarea
              className="aap-field-control aap-field-textarea"
              value={forwardNote}
              disabled={isStepLocked}
              placeholder="Yönlendirme nedenini yazınız"
              onChange={(e) => setForwardNote(e.target.value)}
            />

            {isTouched && !isForwardValid() && (
              <div className="aap-field-error">
                Lütfen hedef birimi seçip yönlendirme notu giriniz.
              </div>
            )}
          </div>
        </div>
      )}

      {/* -------- FOOTER BUTONLARI -------- */}
      <div className="aap-step-footer">
        {/* Geri butonu (gerekirse) */}
        

        <div className="aap-step-footer-actions">
          {/* işleme butonu */}
          <button
            className={`aap-btn ${
              actionType === "forward"
                ? "aap-btn-warning aap-btn-forward-highlight"
                : "aap-btn-primary"
            }`}
            disabled={
              processing ||
              isStepLocked ||
              (actionType === "forward" && !isForwardValid())
            }
            onClick={() => {
              setIsTouched(true);
              openConfirm(actionType);
            }}
          >
            {actionType === "forward" ? "Yönlendir" : "İncelemeye Al"}
          </button>

          {/* İleri butonu */}
          {isStepLocked &&
            actionType === "review" &&
            (applicationData?.status === "işleme_alındı" || successMsg) && (
              <button className="aap-btn aap-btn-ghost" onClick={onNext}>
                Sonraki Adım
              </button>
            )}
        </div>
      </div>

      {/* =========================================================
          ONAY MODALI — Apple Style
      ========================================================= */}
      {showConfirm && (
        <div className="aap-modal-backdrop">
          <div className="aap-modal">
            <div className="aap-modal-icon">
              {confirmType === "forward" ? (
                <ExclamationTriangleIcon />
              ) : (
                <InformationCircleIcon />
              )}
            </div>

            <h3 className="aap-modal-title">
              {confirmType === "forward"
                ? "Başka Birime Yönlendirilsin mi?"
                : "Başvuru İncelemeye Alınsın mı?"}
            </h3>

            <p className="aap-modal-text">
              {confirmType === "forward"
                ? "Bu başvuru başka bir birime aktarılacaktır. Bu işlem geri alınamaz."
                : `Başvuru incelemeye alınacaktır. 
                  }. Bu işlem geri alınamaz.`}
            </p>

            <div className="aap-modal-actions">
              <button
                className="aap-btn aap-btn-secondary"
                onClick={() => setShowConfirm(false)}
              >
                İptal
              </button>

              <button
                className={`aap-btn ${
                  confirmType === "forward"
                    ? "aap-btn-warning aap-btn-forward-highlight"
                    : "aap-btn-primary"
                }`}
                onClick={confirmAction}
              >
                Onayla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewOrForwardStep;
